import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../hooks/useContent';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('quikr_access_token');
  const emailVerified = localStorage.getItem('quikr_email_verified') === 'true';
  const isLoggedIn = Boolean(token && emailVerified);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const { itemCount } = useCart();
  const { language, setLanguage } = useLanguage();
  const { content } = useContent('header');

  const t = content || {
    nav: { home: 'Accueil', pricing: 'Tarifs', product: 'Produits', shop: 'Boutique', dashboard: 'Dashboard', cart: 'Mon panier' },
    auth: { login: 'Connexion', logout: 'Déconnexion' },
    theme: { toDark: 'Passer en mode sombre', toLight: 'Passer en mode clair' },
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('header')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('quikr_access_token');
    localStorage.removeItem('quikr_email_verified');
    document.cookie = 'quikr_access_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-dark-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Quikr.it Logo"
              className="w-36 h-16 object-contain"
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`transition-colors duration-200 ${location.pathname === '/' ? 'text-white' : 'text-gray-300 hover:text-white'}`}
            >
              {t.nav.home}
            </Link>
            <Link
              to="/pricing"
              className={`transition-colors duration-200 ${location.pathname.startsWith('/pricing') ? 'text-white' : 'text-gray-300 hover:text-white'}`}
            >
              {t.nav.pricing}
            </Link>
            <Link
              to="/product"
              className={`transition-colors duration-200 ${location.pathname.startsWith('/product') ? 'text-white' : 'text-gray-300 hover:text-white'}`}
            >
              {t.nav.product}
            </Link>
            <Link
              to="/shop"
              className={`transition-colors duration-200 ${location.pathname.startsWith('/shop') ? 'text-white' : 'text-gray-300 hover:text-white'}`}
            >
              {t.nav.shop}
            </Link>
            {isLoggedIn && (
              <Link
                to="/dashboard"
                className={`flex items-center space-x-1 bg-primary-500/10 border border-primary-500/30 px-3 py-1.5 rounded-lg transition-colors duration-200 ${location.pathname.startsWith('/dashboard') ? 'text-primary-400 border-primary-500' : 'text-primary-400 hover:text-primary-300 hover:border-primary-400'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                <span>{t.nav.dashboard}</span>
              </Link>
            )}
          </nav>

          <div className="flex items-center space-x-4">
            <Link
              to="/cart"
              className="relative hidden sm:flex items-center justify-center w-10 h-10 bg-dark-800 border border-dark-700 rounded-lg hover:bg-dark-700 transition-colors duration-200"
              title={t.nav.cart}
            >
              <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </Link>

            <div className="hidden sm:flex bg-dark-800 border border-dark-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setLanguage('fr')}
                className={`flex items-center px-2 py-1.5 text-xs font-bold transition-colors duration-200 ${
                  language === 'fr' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <img src="/images/flag-fr.svg" alt="FR" className="w-5 h-3.5 rounded-sm object-cover" />
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`flex items-center px-2 py-1.5 text-xs font-bold transition-colors duration-200 ${
                  language === 'en' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <img src="/images/flag-us.svg" alt="EN" className="w-5 h-3.5 rounded-sm object-cover" />
              </button>
            </div>

            <button
              onClick={toggleTheme}
              className="hidden sm:flex items-center justify-center w-10 h-10 bg-dark-800 border border-dark-700 rounded-lg hover:bg-dark-700 transition-colors duration-200"
              title={isDark ? t.theme.toLight : t.theme.toDark}
            >
              {isDark ? (
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            {isLoggedIn ? (
              <button
                onClick={handleLogout}
                className="hidden md:block bg-secondary-600 hover:bg-secondary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
              >
                {t.auth.logout}
              </button>
            ) : (
              <Link
                to="/login"
                className="hidden md:block bg-primary-500 hover:bg-primary-600 text-black px-4 py-2 rounded-lg font-medium transition-colors duration-200"
              >
                {t.auth.login}
              </Link>
            )}
          </div>

          <button
            className="md:hidden text-gray-300 hover:text-white transition-colors duration-200"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-black/95 backdrop-blur-sm border-b border-dark-700 shadow-lg">
            <div className="px-4 py-6 space-y-4">
              <Link
                to="/"
                className={`block py-2 transition-colors duration-200 ${
                  location.pathname === '/' ? 'text-white font-semibold' : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.nav.home}
              </Link>
              <Link
                to="/pricing"
                className={`block py-2 transition-colors duration-200 ${
                  location.pathname.startsWith('/pricing') ? 'text-white font-semibold' : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.nav.pricing}
              </Link>
              <Link
                to="/product"
                className={`block py-2 transition-colors duration-200 ${
                  location.pathname.startsWith('/product') ? 'text-white font-semibold' : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.nav.product}
              </Link>
              <Link
                to="/shop"
                className={`block py-2 transition-colors duration-200 ${
                  location.pathname.startsWith('/shop') ? 'text-white font-semibold' : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.nav.shop}
              </Link>

              {isLoggedIn && (
                <Link
                  to="/dashboard"
                  className={`flex items-center space-x-2 py-2 mt-2 px-3 bg-primary-500/10 border border-primary-500/30 rounded-lg transition-colors duration-200 ${
                    location.pathname.startsWith('/dashboard') ? 'text-primary-400 border-primary-500' : 'text-primary-400 hover:text-primary-300'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  <span>{t.nav.dashboard}</span>
                </Link>
              )}

              <Link
                to="/cart"
                className={`flex items-center space-x-2 py-2 mt-2 px-3 bg-dark-800 border border-dark-700 rounded-lg transition-colors duration-200 ${
                  location.pathname === '/cart' ? 'text-white border-primary-500' : 'text-gray-300 hover:text-white hover:border-dark-600'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>{t.nav.cart}</span>
                {itemCount > 0 && (
                  <span className="ml-auto px-2 py-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-xs font-bold rounded-full">
                    {itemCount}
                  </span>
                )}
              </Link>

              <div className="pt-4 border-t border-dark-700">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-400 text-sm">{language === 'fr' ? 'Langue :' : 'Language:'}</span>
                  <div className="flex bg-dark-800 border border-dark-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setLanguage('fr')}
                      className={`flex items-center px-2 py-1.5 text-xs font-bold transition-colors duration-200 ${
                        language === 'fr' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      <img src="/images/flag-fr.svg" alt="FR" className="w-5 h-3.5 rounded-sm object-cover" />
                      <span>FR</span>
                    </button>
                    <button
                      onClick={() => setLanguage('en')}
                      className={`flex items-center px-2 py-1.5 text-xs font-bold transition-colors duration-200 ${
                        language === 'en' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      <img src="/images/flag-us.svg" alt="EN" className="w-5 h-3.5 rounded-sm object-cover" />
                      <span>EN</span>
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-400 text-sm">{language === 'fr' ? 'Thème :' : 'Theme:'}</span>
                  <button
                    onClick={toggleTheme}
                    className="flex items-center justify-center w-10 h-10 bg-dark-800 border border-dark-700 rounded-lg hover:bg-dark-700 transition-colors duration-200"
                    title={isDark ? t.theme.toLight : t.theme.toDark}
                  >
                    {isDark ? (
                      <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                    )}
                  </button>
                </div>

                {isLoggedIn ? (
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-secondary-600 hover:bg-secondary-700 text-white px-4 py-3 rounded-lg font-medium transition-colors duration-200"
                  >
                    {t.auth.logout}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="block w-full bg-primary-500 hover:bg-primary-600 text-black px-4 py-3 rounded-lg font-medium transition-colors duration-200 text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t.auth.login}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
